import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { HomeAPI, NewsAPI, DocumentsAPI, BoardAPI, FunktionarerAPI, ContactEntriesAPI, PagesAPI } from '../services/api';
import Markdown from '../components/Markdown';

const SCROLL_STORAGE_KEY = 'fornbo_scroll_positions';

const safeParseScrollPositions = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

// Store scroll position before navigating to sub-page
const storeScrollPosition = () => {
  const scrollPositions = safeParseScrollPositions();
  scrollPositions['home'] = window.scrollY;
  scrollPositions['timestamp'] = Date.now();
  sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(scrollPositions));
};

// Wrapper for Link that stores scroll position
function ScrollStoreLink({ to, children, ...props }) {
  const handleClick = useCallback((e) => {
    storeScrollPosition();
    if (props.onClick) props.onClick(e);
  }, [props.onClick]);

  return (
    <Link to={to} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
}

function fmtDatum(s) {
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const m = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
    return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return s;
  }
}

function NewsCard({ item, onOpen }) {
  const textRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [item.text]);

  return (
    <div
      className="card"
      onClick={onOpen}
      style={{
        borderColor: item.pinned ? 'var(--accent)' : 'var(--border)',
        borderLeft: item.pinned ? '4px solid var(--accent)' : undefined,
        cursor: 'pointer'
      }}
    >
      <div style={{
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--text-soft)',
        marginBottom: '0.5rem'
      }}>
        {fmtDatum(item.datum)}
      </div>
      <h3 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1.05rem',
        color: 'var(--forest)',
        marginBottom: '0.6rem',
        lineHeight: 1.35
      }}>
        {item.titel}
      </h3>
      <div ref={textRef} style={{ position: 'relative', maxHeight: '13rem', overflow: 'hidden' }}>
        <Markdown content={item.text} />
        {overflows && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '3rem',
            background: 'linear-gradient(to bottom, transparent, var(--white))',
            pointerEvents: 'none'
          }} />
        )}
      </div>
      {item.tagg && (
        <span style={{
          display: 'inline-block',
          marginTop: '1rem',
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          padding: '0.2rem 0.6rem',
          borderRadius: '3px',
          background: 'var(--cream)',
          color: 'var(--text-soft)'
        }}>
          {item.tagg}
        </span>
      )}
    </div>
  );
}

export default function Home() {
  // News section (priority - loads first)
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(null);

  // Other sections (load after news)
  const [otherData, setOtherData] = useState({
    documents: [],
    board: [],
    funktionarer: [],
    contactEntries: [],
    pages: []
  });
  const [otherLoading, setOtherLoading] = useState(true);
  const [otherError, setOtherError] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);

  const location = useLocation();
  const navigationType = useNavigationType();

  // Overall loading state for scroll handling
  const loading = newsLoading || otherLoading;

  // Handle hash navigation after data loads
  useEffect(() => {
    if (loading) return;
    if (navigationType === 'POP') return;
    if (!location.hash) return;
    try {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {}
  }, [loading, location.hash, navigationType]);

  // Track current section with IntersectionObserver for nav highlighting
  useEffect(() => {
    if (loading) return;

    const sectionIds = ['aktuellt', 'ansvarsomraden', 'sjon', 'foreningen', 'dokument', 'kontakt'];
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          sessionStorage.setItem('current_section', sectionId);
          window.dispatchEvent(new CustomEvent('sectionChange', { detail: sectionId }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  // Fetch all home data in one cached request
  useEffect(() => {
    const navType = navigationType;
    const currentHash = location.hash;

    const fetchAll = async () => {
      try {
        setNewsLoading(true);
        setOtherLoading(true);
        setNewsError(null);
        setOtherError(null);

        let data;
        try {
          data = await HomeAPI.get();
        } catch (e) {
          // Fallback to individual API calls
          const [newsRes, docsRes, boardRes, funktionarerRes, contactEntriesRes, pagesRes] = await Promise.all([
            NewsAPI.getAll(),
            DocumentsAPI.getAll(),
            BoardAPI.getAll(),
            FunktionarerAPI.getAll(),
            ContactEntriesAPI.getAll(),
            PagesAPI.getAll()
          ]);

          data = {
            news: newsRes.news || [],
            documents: (docsRes.documents || [])
              .filter(d => !(d.is_protected || d.isProtected))
              .sort((a, b) => (b.datum || '').localeCompare(a.datum || ''))
              .slice(0, 3),
            board: boardRes.board || [],
            funktionarer: funktionarerRes.funktionarer || [],
            contactEntries: contactEntriesRes.entries || [],
            pages: pagesRes.pages || []
          };
        }

        const sortedNews = (data.news || []).sort((a, b) => {
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
          return new Date(b.datum) - new Date(a.datum);
        });

        setNews(sortedNews.slice(0, 4));
        setOtherData(data);
      } catch (err) {
        console.error('Failed to fetch home data:', err);
        setNewsError(err.message);
        setOtherError(err.message);
      } finally {
        setNewsLoading(false);
        setOtherLoading(false);

        // Restore scroll position (if POP navigation)
        if (navType === 'POP') {
          const scrollPositions = safeParseScrollPositions();
          const savedPosition = scrollPositions['home'];
          if (savedPosition !== undefined && savedPosition >= 0) {
            requestAnimationFrame(() => {
              window.scrollTo({ top: savedPosition, behavior: 'auto' });
            });
          } else if (currentHash) {
            requestAnimationFrame(() => {
              try {
                const el = document.querySelector(currentHash);
                if (el) el.scrollIntoView({ behavior: 'auto' });
              } catch {}
            });
          }
        }
      }
    };

    fetchAll();
  }, []);

  useEffect(() => {
    if (!selectedNews) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setSelectedNews(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [selectedNews]);

  return (
    <>
      {/* Hero Section */}
      <section style={{
        height: '100svh',
        minHeight: '540px',
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(to bottom, rgba(20,30,22,.08) 0%, rgba(15,22,16,.65) 100%), url('/Fornbo.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%'
        }} />
        <div style={{
          position: 'relative',
          zIndex: 1,
          padding: '3rem 3rem 4rem',
          maxWidth: '700px'
        }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--sand)',
            marginBottom: '1rem'
          }}>
            Välkommen till
          </div>
          <h1 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(2.4rem, 5vw, 4rem)',
            color: '#fff',
            lineHeight: 1.15,
            marginBottom: '1.25rem'
          }}>
            Fornbo<br />
            <em style={{ fontStyle: 'italic', color: 'var(--sky)' }}>Samfällighet</em>
          </h1>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(255,255,255,.82)',
            maxWidth: '480px',
            marginBottom: '2rem',
            lineHeight: 1.75
          }}>
            Ett naturskönt område med sjön Nedingen inpå knuten. Vi förvaltar gemensamma vägar, grönområden och badplatser för alla fastighetsägare i Fornbo.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <Link to="/#aktuellt" className="btn btn-primary">Senaste nytt</Link>
            <Link to="/#kontakt" className="btn btn-ghost" style={{ borderColor: 'rgba(255,255,255,.5)', color: '#fff' }}>
              Kontakta styrelsen
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <div style={{
        background: 'var(--forest)',
        padding: '1.25rem 2rem',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '0.25rem'
      }}>
        <QuickLink href="#sjon">🦞 Kräftfiske</QuickLink>
        <QuickLink href="#dokument">📰 Fornbobladet</QuickLink>
        <QuickLink href="#foreningen">📄 Stadgar</QuickLink>
        <QuickLink href="#kontakt">✉️ Kontakt</QuickLink>
        <QuickLink href="/styrelsen">🔒 Styrelsen</QuickLink>
      </div>

      {/* News Section */}
      <section id="aktuellt" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <SectionHeader
            label="Nyheter & information"
            title="Aktuellt i Fornbo"
            lead="Det senaste från styrelsen och föreningen. Uppdateras löpande under året."
          />

          {newsLoading ? (
            <div className="loading">Laddar nyheter...</div>
          ) : newsError ? (
            <div className="error">Kunde inte ladda nyheter: {newsError}</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))',
              gap: '1.5rem'
            }}>
              {news.map((item) => (
                <NewsCard key={item.id} item={item} onOpen={() => setSelectedNews(item)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Areas Section */}
      <section id="ansvarsomraden" style={{ background: 'var(--white)' }}>
        <div className="container">
          <SectionHeader
            label="Gemensam förvaltning"
            title="Ansvarsområden"
            lead="Samfälligheten förvaltar gemensamma anläggningar och ytor för alla fastighetsägare."
          />

          {otherLoading ? (
            <div className="loading" style={{ color: 'var(--text-soft)' }}>Laddar...</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '1.25rem'
            }}>
              {otherData.pages
                .filter(p => p.category === 'ansvarsomraden')
                .sort((a, b) => a.title.localeCompare(b.title, 'sv'))
                .map(page => (
                  <AreaCard
                    key={page.slug}
                    to={`/ansvarsomraden/${page.slug}`}
                    icon={page.icon || getAreaIcon(page.slug)}
                    name={page.title}
                    desc={page.description || getAreaDescription(page.slug)}
                  />
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Lake Section */}
      <section id="sjon" style={{
        background: 'var(--lake)',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          content: "''",
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, var(--sky), var(--forest), var(--sky))'
        }} />
        <div className="container">
          <SectionHeader
            label="Naturupplevelser"
            title="Sjön Nedingen"
            lead="Nedingen är ett av Fornbos stora tillgångar — med fiske, kräftfiske, bryggor och klart vatten."
            light
          />

          {otherLoading ? (
            <div style={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', padding: '2rem' }}>Laddar...</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '1rem'
            }}>
              {otherData.pages
                .filter(p => p.category === 'nedingen')
                .sort((a, b) => a.title.localeCompare(b.title, 'sv'))
                .map(page => (
                  <LakeCard
                    key={page.slug}
                    to={`/nedingen/${page.slug}`}
                    icon={page.icon || getLakeIcon(page.slug)}
                    title={page.title}
                    desc={page.description || getLakeDescription(page.slug)}
                  />
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Association Section */}
      <section id="foreningen" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <SectionHeader
            label="Om oss"
            title="Föreningen"
            lead="Fornbo Samfällighetsförening förvaltar gemensamma anläggningar och intressen för alla i Fornbo."
          />

          {otherLoading ? (
            <div className="loading" style={{ color: 'var(--text-soft)' }}>Laddar...</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '1.25rem'
            }}>
              {otherData.pages
                .filter(p => p.category === 'foreningen')
                .sort((a, b) => a.title.localeCompare(b.title, 'sv'))
                .map(page => (
                  <ForeningCard
                    key={page.slug}
                    to={`/foreningen/${page.slug}`}
                    icon={page.icon || getForeningIcon(page.slug)}
                    name={page.title}
                  />
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Documents Section */}
      <section id="dokument" style={{ background: 'var(--white)' }}>
        <div className="container">
          <SectionHeader
            label="Ladda ner"
            title="Dokument"
            lead="Föreningens dokument och Fornbobladet — senaste numren samlade."
          />

          {otherLoading ? (
            <div className="loading" style={{ color: 'var(--text-soft)' }}>Laddar dokument...</div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              maxWidth: '640px'
            }}>
              {otherData.documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.9rem',
                  padding: '0.9rem 1.1rem',
                  border: '1px solid var(--border)',
                  borderRadius: '5px',
                  background: 'var(--cream)',
                  textDecoration: 'none',
                  color: 'var(--text)'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: 'rgba(212,132,60,.12)',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {doc.kategori === 'Fornbobladet' ? '📰' : doc.kategori === 'Protokoll' ? '📄' : '📋'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '0.88rem', 
                    color: 'var(--text-mid)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>{doc.namn}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)' }}>
                    {doc.kategori} · {doc.datum}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const response = await DocumentsAPI.download(doc.id);
                      let blobUrl, filename;
                      if (response.url) {
                        const fileRes = await fetch(response.url);
                        if (!fileRes.ok) throw new Error(`Storage error: ${fileRes.status}`);
                        const blob = await fileRes.blob();
                        blobUrl = window.URL.createObjectURL(blob);
                        filename = response.filename || doc.namn;
                      } else if (response.file) {
                        const byteArray = Uint8Array.from(atob(response.file), c => c.charCodeAt(0));
                        const blob = new Blob([byteArray], { type: response.contentType });
                        blobUrl = window.URL.createObjectURL(blob);
                        filename = response.filename || doc.namn;
                      }
                      if (blobUrl) {
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
                      }
                    } catch (err) {
                      console.error('Download failed:', err);
                      alert('Kunde inte ladda ner filen. ' + (err.message || ''));
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.4rem 0.75rem',
                    background: 'var(--forest)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    flexShrink: 0,
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(44,74,46,0.85)'}
                  onMouseLeave={e => e.target.style.background = 'var(--forest)'}
                >
                  ⬇ Ladda ner
                </button>
              </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '1.5rem' }}>
            <ScrollStoreLink
              to="/dokument"
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Visa alla dokument
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </ScrollStoreLink>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <SectionHeader
            label="Hör av dig"
            title="Kontakta oss"
            lead="Kontakta styrelsen direkt via telefon eller e-post. Vi svarar normalt inom 1–2 dagar."
          />

          {otherLoading ? (
            <div className="loading" style={{ color: 'var(--text-soft)' }}>Laddar kontaktuppgifter...</div>
          ) : (
            <>
              {/* Kontaktuppgifter - Emails and Postal Address */}
              {otherData.contactEntries.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--forest)', marginBottom: '0.75rem', fontFamily: '"Playfair Display", serif' }}>
                    Kontaktuppgifter
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                    {otherData.contactEntries.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((entry) => {
                  if (entry.type === 'postal_address') {
                    return (
                      <div key={entry.id} style={{
                        padding: '0.9rem 1.1rem',
                        border: '1px solid var(--border)',
                        borderRadius: '5px',
                        background: 'var(--white)'
                      }}>
                        <div style={{
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: 'var(--text-soft)',
                          marginBottom: '0.3rem'
                        }}>
                          {entry.label}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          color: 'var(--text)',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.5
                        }}>
                          📍 {entry.value}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={entry.id} style={{
                      padding: '0.9rem 1.1rem',
                      border: '1px solid var(--border)',
                      borderRadius: '5px',
                      background: 'var(--white)'
                    }}>
                      <div style={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'var(--text-soft)',
                        marginBottom: '0.3rem'
                      }}>
                        {entry.label}
                      </div>
                      <div style={{ marginBottom: entry.description ? '0.5rem' : 0 }}>
                        <a
                          href={`mailto:${entry.value}`}
                          style={{
                            fontSize: '0.95rem',
                            color: 'var(--lake)',
                            fontFamily: 'monospace',
                            textDecoration: 'none'
                          }}
                        >
                          📧 {entry.value}
                        </a>
                      </div>
                      {entry.description && (
                        <div style={{
                          fontSize: '0.78rem',
                          color: 'var(--text-soft)',
                          lineHeight: 1.4
                        }}>
                          {entry.description}
                        </div>
                      )}
                    </div>
                  );
                })}
                  </div>
                </div>
              )}

              {/* Styrelse */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--forest)', marginBottom: '0.75rem', fontFamily: '"Playfair Display", serif' }}>
                  Styrelse
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  marginBottom: '1.5rem'
                }}>
                  {otherData.board.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((member, idx) => {
                const accent = member.color || '#2c4a2e';
                const isVacant = !member.namn || member.namn.toLowerCase().startsWith('vakant');
                return (
                  <div key={idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0.9rem 1.1rem',
                    border: '1px solid var(--border)',
                    borderLeft: `4px solid ${isVacant ? 'var(--border)' : accent}`,
                    borderRadius: '5px',
                    background: 'var(--white)'
                  }}>
                    <div style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: accent,
                      marginBottom: '0.2rem'
                    }}>
                      {member.roll}
                    </div>
                    <div style={{
                      fontSize: '0.95rem',
                      color: 'var(--text)',
                      fontFamily: '"Playfair Display", serif'
                    }}>
                      {member.namn}
                    </div>
                    {member.beskrivning && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginTop: '0.15rem' }}>
                        {member.beskrivning}
                      </div>
                    )}
                    {member.adress && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginTop: '0.15rem' }}>
                        📍 {member.adress}
                      </div>
                    )}
                    <div style={{
                      fontSize: '0.78rem',
                      color: 'var(--text-soft)',
                      marginTop: '0.2rem'
                    }}>
                      {member.telefon && (
                        <a href={`tel:${member.telefon.replace(/[\s-]/g, '')}`} style={{ color: 'var(--lake)' }}>
                          📞 {member.telefon}
                        </a>
                      )}
                      {member.epost && (
                        <span>{member.telefon ? ' · ' : ''}<a href={`mailto:${member.epost}`} style={{ color: 'var(--lake)' }}>✉ {member.epost}</a></span>
                      )}
                      {!member.telefon && !member.epost && isVacant && 'Se Aktuellt — valberedningens utlysning'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

              {/* Funktionärer */}
              {otherData.funktionarer.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--forest)', marginBottom: '0.75rem', fontFamily: '"Playfair Display", serif' }}>
                    Funktionärer
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.6rem',
                    marginBottom: '1.5rem'
                  }}>
                    {otherData.funktionarer.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((member, idx) => {
                  const accent = member.color || '#5a6068';
                  const isVacant = !member.namn || member.namn.toLowerCase().startsWith('vakant');
                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--border)',
                      borderLeft: `3px solid ${isVacant ? 'var(--border)' : accent}`,
                      borderRadius: '4px',
                      background: 'var(--white)'
                    }}>
                      <div style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: accent,
                        marginBottom: '0.15rem'
                      }}>
                        {member.roll}
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: isVacant ? 'var(--text-soft)' : 'var(--text)'
                      }}>
                        {member.namn || '—'}
                      </div>
                      {member.beskrivning && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)', marginTop: '0.1rem' }}>
                          {member.beskrivning}
                        </div>
                      )}
                      <div style={{
                        fontSize: '0.7rem',
                        color: 'var(--text-soft)',
                        marginTop: '0.15rem'
                      }}>
                        {member.adress && <span>📍 {member.adress}</span>}
                        {member.telefon && <span style={{ marginLeft: member.adress ? '0.5rem' : 0 }}>📞 {member.telefon}</span>}
                        {member.epost && <span style={{ marginLeft: member.telefon || member.adress ? '0.5rem' : 0 }}>✉ {member.epost}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </section>

      {selectedNews && (
        <div
          onClick={() => setSelectedNews(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
            zIndex: 1000, overflowY: 'auto', display: 'flex',
            alignItems: 'flex-start', padding: '2rem 1rem'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--white)', borderRadius: 8, width: '100%',
              maxWidth: 680, margin: '0 auto', padding: '2rem',
              position: 'relative', maxHeight: '90vh', overflowY: 'auto'
            }}
          >
            <button
              onClick={() => setSelectedNews(null)}
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.5rem', color: 'var(--text-soft)', lineHeight: 1,
                padding: '0.25rem 0.5rem'
              }}
            >×</button>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              marginBottom: '0.75rem'
            }}>
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'var(--text-soft)'
              }}>
                {fmtDatum(selectedNews.datum)}
              </span>
              {selectedNews.tagg && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em',
                  textTransform: 'uppercase', padding: '0.2rem 0.6rem',
                  borderRadius: 3, background: 'var(--cream)', color: 'var(--text-soft)'
                }}>
                  {selectedNews.tagg}
                </span>
              )}
              {selectedNews.pinned && (
                <span style={{ fontSize: '0.8rem' }}>📌</span>
              )}
            </div>
            <h2 style={{
              fontFamily: '"Playfair Display", serif', fontSize: '1.4rem',
              color: 'var(--forest)', marginBottom: '1.25rem', lineHeight: 1.3
            }}>
              {selectedNews.titel}
            </h2>
            <Markdown content={selectedNews.text} />
          </div>
        </div>
      )}
    </>
  );
}

function SectionHeader({ label, title, lead, light = false }) {
  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <div style={{
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: light ? 'var(--sky)' : 'var(--moss)',
        marginBottom: '0.6rem'
      }}>
        {label}
      </div>
      <h2 style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
        color: light ? '#fff' : 'var(--forest)',
        lineHeight: 1.25,
        marginBottom: '0.5rem'
      }}>
        {title}
      </h2>
      <div style={{
        width: '48px',
        height: '3px',
        background: 'var(--sand)',
        borderRadius: '2px',
        margin: '1rem 0 1.5rem'
      }} />
      <p style={{
        color: light ? 'rgba(255,255,255,.75)' : 'var(--text-soft)',
        fontSize: '1rem',
        maxWidth: '560px',
        lineHeight: 1.75
      }}>
        {lead}
      </p>
    </div>
  );
}

function QuickLink({ href, children }) {
  return (
    <Link
      to={href.startsWith('#') ? `/${href}` : href}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: 'rgba(255,255,255,.75)',
        textDecoration: 'none',
        fontSize: '0.78rem',
        fontWeight: 400,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '0.5rem 1.1rem',
        borderRadius: '3px',
        transition: 'background 0.18s, color 0.18s'
      }}
    >
      {children}
    </Link>
  );
}

function AreaCard({ to, icon, name, desc }) {
  return (
    <ScrollStoreLink to={to} style={{
      background: 'var(--cream)',
      border: '1px solid var(--border)',
      borderRadius: '6px',
      padding: '1.75rem 1.4rem',
      textAlign: 'center',
      textDecoration: 'none',
      color: 'var(--text)',
      transition: 'background 0.2s, box-shadow 0.2s',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.6rem'
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        background: 'var(--forest)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.35rem',
        marginBottom: '0.25rem'
      }}>
        {icon}
      </div>
      <div style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1rem',
        color: 'var(--forest)',
        lineHeight: 1.3
      }}>
        {name}
      </div>
      <div style={{
        fontSize: '0.8rem',
        color: 'var(--text-soft)',
        lineHeight: 1.5
      }}>
        {desc}
      </div>
    </ScrollStoreLink>
  );
}

function LakeCard({ to, icon, title, desc }) {
  return (
    <ScrollStoreLink to={to} style={{
      background: 'rgba(255,255,255,.1)',
      border: '1px solid rgba(255,255,255,.2)',
      borderRadius: '6px',
      padding: '1.5rem',
      textDecoration: 'none',
      color: '#fff',
      transition: 'background 0.2s'
    }}>
      <div style={{ fontSize: '1.6rem', marginBottom: '0.6rem' }}>{icon}</div>
      <div style={{
        fontFamily: '"Playfair Display", serif',
        fontSize: '1rem',
        marginBottom: '0.4rem'
      }}>
        {title}
      </div>
      <div style={{
        fontSize: '0.82rem',
        color: 'rgba(255,255,255,.7)',
        lineHeight: 1.6
      }}>
        {desc}
      </div>
    </ScrollStoreLink>
  );
}

// Helper functions for icons
function getAreaIcon(slug) {
  const icons = {
    vatten: '💧',
    skog: '🌲',
    vagar: '🛣️',
    bygg: '🏗️',
    bad: '🏊'
  };
  return icons[slug] || '📄';
}

function getAreaDescription(slug) {
  const descriptions = {
    vatten: 'Dricksvatten och VA-frågor',
    skog: 'Promenadstigar och grönområden',
    vagar: 'Vägunderhåll och gemensamhetsvägar',
    bygg: 'Byggfrågor och dagvattenhantering',
    bad: 'Badplatser, fotbollsplan och bryggor'
  };
  return descriptions[slug] || 'Läs mer';
}

function getLakeIcon(slug) {
  const icons = {
    fiske: '🎣',
    kraftfiske: '🦞',
    vattenprover: '🧪',
    bryggforeningen: '⛵'
  };
  return icons[slug] || '📄';
}

function getLakeDescription(slug) {
  const descriptions = {
    fiske: 'Information om fiske i Nedingen',
    kraftfiske: 'Burar, tider och regler',
    vattenprover: 'Aktuella vattenkvalitetsmätningar',
    bryggforeningen: 'Bryggplatser och information'
  };
  return descriptions[slug] || 'Läs mer';
}

function getForeningIcon(slug) {
  const icons = {
    stadgar: '📜',
    trivselregler: '🤝',
    stammor: '📊',
    fakta: '📋',
    valkomstbrev: '✉️',
    motionera: '📝',
    verksamhetsidee: '💡',
    detaljplan: '🗺️',
    anlaggningsbeslutet: '⚖️',
    'kommunalt-va': '🏠'
  };
  return icons[slug] || '📄';
}

function ForeningCard({ to, icon, name }) {
  return (
    <ScrollStoreLink to={to} style={{
      background: 'var(--white)',
      border: '1px solid var(--border)',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '1.2rem 1.4rem',
      textDecoration: 'none',
      color: 'var(--text)',
      transition: 'box-shadow 0.2s'
    }}>
      <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: '0.88rem', color: 'var(--text-mid)' }}>{name}</span>
    </ScrollStoreLink>
  );
}
